import type { TaskListBase } from '@awork/features/task/models/task-list.model.base'
import type { UserBase } from '@awork/features/user/models/user.model.base'

export interface IRecurringTask {
  baseTaskId: string
  name: string
  baseType: string
  entityId: string
  plannedDuration?: number
  occurrences: { startOn: Date; dueOn: Date }[]
  lists: TaskListBase[]
  assignees: UserBase[]
  createdOn?: Date | string
  startDate?: Date
  endDate?: Date
  laneOrder?: number
  autoLaneOrder?: number
  entityType: string
  id: string
}

export class RecurringTask implements IRecurringTask {
  baseTaskId: string
  name: string
  baseType: string
  entityId: string
  plannedDuration?: number
  occurrences: { startOn: Date; dueOn: Date }[]
  lists: TaskListBase[]
  assignees: UserBase[]
  createdOn?: Date | string
  startDate?: Date
  endDate?: Date
  laneOrder?: number
  autoLaneOrder?: number
  entityType: string
  id: string

  constructor(recurringTask: Partial<IRecurringTask>) {
    Object.assign(this, recurringTask)

    this.entityType = 'RecurringTask'

    if (typeof recurringTask.startDate === 'string') {
      this.startDate = new Date(this.startDate)
    }

    if (typeof recurringTask.endDate === 'string') {
      this.endDate = new Date(this.endDate)
    }

    if (typeof recurringTask.createdOn === 'string') {
      this.createdOn = new Date(this.createdOn)
    }

    if (this.occurrences) {
      for (const occurence of this.occurrences) {
        if (typeof occurence.startOn === 'string') {
          occurence.startOn = new Date(occurence.startOn)
        }

        if (typeof occurence.dueOn === 'string') {
          occurence.dueOn = new Date(occurence.dueOn)
        }
      }
    }
  }

  /**
   * Returns the start date of the recurring task
   * @returns {Date}
   */
  get startOn(): Date {
    return this.startDate ? new Date(this.startDate) : undefined
  }

  /**
   * Sets the start date of the recurring task
   * @param {Date} value
   */
  set startOn(value: Date) {
    this.startDate = value
  }

  /**
   * Returns the end date of the recurring task
   * @returns {Date}
   */
  get endOn(): Date {
    return this.endDate ? new Date(this.endDate) : undefined
  }

  /**
   * Sets the end date of the recurring task
   * @param {Date} value
   */
  set endOn(value: Date) {
    this.endDate = value
  }

  /**
   * Compares a group of assignees with the recurrency's assignees
   * @param {UserBase[]} assigneesToCompare
   * @returns {boolean}
   */
  hasSameAssignees(assigneesToCompare: UserBase[]): boolean {
    if (!assigneesToCompare?.length && !this.assignees?.length) {
      return true
    }

    if (assigneesToCompare?.length !== this.assignees?.length) {
      return false
    }

    const usersIdsSet = new Set(assigneesToCompare.map(user => user.id))

    return this.assignees.every(user => user?.id && usersIdsSet.has(user.id))
  }
}
